import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';

/**
 * Normaliza string: lowercase, remove acentos, substitui pontuação por espaço.
 */
const normalizar = (texto: string): string =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Rótulos canônicos na ordem definida.
 */
const ROTULOS_CANONICOS = [
  'Assistência / Orientação Técnica',
  'Assistência Técnica e Extensão Rural - ATER',
  'Horas-trator / Máquina (Com custo)',
  'Horas-trator / Máquina (Gratuitamente)',
  'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)',
  'Apoio à elaboração de projetos de crédito rural',
  'Realização de eventos (Dias de campo, cursos, seminários)',
  'Associativismo e Cooperativismo',
  'Acesso à água para consumo',
  'Irrigação',
  'Avicultura',
  'Apicultura',
  'Orizicultura (Arroz na agricultura familiar)',
  'Cacauicultura',
  'Cafeicultura',
  'Floricultura',
  'Fruticultura',
  'Mandiocultura',
  'Olericultura',
  'Pecuária de Leite',
  'Pecuária de Corte',
  'Piscicultura',
  'Sistemas Agroflorestais (SAFs)',
  'Plantas Medicinais, Aromáticas e Condimentares',
  'Produção Agroecológica',
  'Produção Orgânica',
  'Serviço de Inspeção Municipal - SIM',
  'Apoio à realização de feiras (Livres e/ou Permanentes)',
  'Apoio ao PNAE (Merenda Escolar)',
  'Apoio ao PAA (Aquisição de Alimentos Federal)',
  'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)',
  'Compras Governamentais Municipais',
  'Comercialização em Canais Convencionais (Mercados, Hotéis)',
  'Comercialização Eletrônica (Sites, Apps)',
  'Apoio na emissão de Nota Fiscal',
  'Declaração de Produção para Inscrição Estadual',
  'Ações para Juventude Rural',
  'Ações para Mulheres Rurais',
  'Ações para Povos Indígenas',
  'Ações para Quilombolas',
  'Ações para Outros Povos e Comunidades',
  'Artesanato Rural',
  'Geoprocessamento',
  'Licenciamento Ambiental',
  'Cadastro Ambiental Rural - CAR',
  'Preservação Ambiental',
  'Extrativismo e Sociobiodiversidade',
  'Manejo Ambiental',
  'Recuperação de Áreas Degradadas',
  'Economia Solidária',
  'Segurança Alimentar e Nutricional',
];

/**
 * Mapeamento explícito de nomes verbosos da API para rótulos canônicos.
 * Chaves são normalizadas (sem acentos, lowercase, sem pontuação).
 */
const MAPEAMENTO_API: Record<string, string> = {
  'disponibilizacao de horas trator maquina com custo':
    'Horas-trator / Máquina (Com custo)',
  'disponibilizacao de horas trator maquina gratuitamente':
    'Horas-trator / Máquina (Gratuitamente)',
  'disponibilizacao de insumos calcario fertilizantes sementes mudas etc':
    'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)',
  'programa projeto acao voltada a cadeia produtiva do arroz na agricultura familiar':
    'Orizicultura (Arroz na agricultura familiar)',
  'programa projeto acao voltada a cadeia produtiva da mandioca':
    'Mandiocultura',
  'apoio a elaboracao de projetos para o programa nacional de alimentacao escolar pnae':
    'Apoio ao PNAE (Merenda Escolar)',
  'apoio a elaboracao de propostas para o programa de aquisicao de alimentos paa':
    'Apoio ao PAA (Aquisição de Alimentos Federal)',
  'programa municipal de aquisicao de alimentos com recursos proprios':
    'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)',
  'programa municipal de compras governamentais aquisicao de produtos da agricultura familiar para atender os orgaos da administracao publica municipal':
    'Compras Governamentais Municipais',
  'programa projeto acao voltada a comercializacao dos produtos da agricultura familiar nos canais de comercializacao convencionais supermercados quitandas restaurantes bares lanchonetes hoteis etc':
    'Comercialização em Canais Convencionais (Mercados, Hotéis)',
  'programa projeto acao voltada a comercializacao eletronica dos produtos da agricultura familiar aplicativos sites midias sociais etc':
    'Comercialização Eletrônica (Sites, Apps)',
  'emissao de declaracao de producao e localizacao de estabelecimento agropecuario para beneficiario da reforma agraria com vistas a obtencao de inscricao estadual':
    'Declaração de Produção para Inscrição Estadual',
  'programa projeto acao especifica para a juventude rural':
    'Ações para Juventude Rural',
  'programa projeto acao especifica para as mulheres rurais':
    'Ações para Mulheres Rurais',
  'programa projeto acao especifica para os indigenas':
    'Ações para Povos Indígenas',
  'programa projeto acao especifica para os quilombolas':
    'Ações para Quilombolas',
  'programa projeto acao especifica para outros povos e comunidades tradicionais':
    'Ações para Outros Povos e Comunidades',
  'programa projeto acao voltada ao artesanato':
    'Artesanato Rural',
  'programa projeto acao voltada ao extrativismo de produtos florestais nao madeireiros sociobiodiversidade':
    'Extrativismo e Sociobiodiversidade',
  'recuperacao recomposicao ambiental de areas degradadas alteradas':
    'Recuperação de Áreas Degradadas',
};
// Adiciona entradas automáticas dos rótulos canônicos
ROTULOS_CANONICOS.forEach((rotulo) => {
  const chave = normalizar(rotulo);
  if (!MAPEAMENTO_API[chave]) MAPEAMENTO_API[chave] = rotulo;
});
MAPEAMENTO_API['nenhum servico e prestado'] = 'Nenhum serviço é prestado';
MAPEAMENTO_API['outros'] = 'Outros';

/**
 * Retorna o rótulo canônico para um nome_servico vindo da API.
 */
const getRotuloServico = (nomeServico: string): string => {
  const norm = normalizar(nomeServico);

  if (MAPEAMENTO_API[norm]) return MAPEAMENTO_API[norm];

  for (const [chave, rotulo] of Object.entries(MAPEAMENTO_API)) {
    if (norm.includes(chave) && chave.length > 5) return rotulo;
  }

  for (const [chave, rotulo] of Object.entries(MAPEAMENTO_API)) {
    if (chave.includes(norm) && norm.length > 5) return rotulo;
  }

  return nomeServico;
};

const Step4View: React.FC = () => {
  const { formulario } = useFormularioSecretariaMunicipalAgriculturaStore();
  const [open, setOpen] = useState<boolean>(true);

  if (!formulario) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Carregando informações...</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 0, mt: 2 }}>
      <Box
        sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "#000000DE",
              fontSize: "20px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {open ? (
              <KeyboardArrowUpOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            ) : (
              <KeyboardArrowDownOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            )}
            Quais são os serviços prestados por essa Secretária/Pasta de Agricultura?
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
      </Box>

      {open && (
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {formulario.servicos_prestados_descritivo && formulario.servicos_prestados_descritivo.length > 0 ? (
                <List>
                  {formulario.servicos_prestados_descritivo.map((servicoItem, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={getRotuloServico(servicoItem.servicos_prestados_orgao_agricultura.nome_servico)}
                        primaryTypographyProps={{
                          variant: 'body1',
                          fontWeight: 500
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    Nenhum serviço prestado foi informado para esta secretaria/órgão.
                  </Typography>
                </Alert>
              )}
            </Grid>

            {formulario.outro_servico_prestado && formulario.outro_servico_prestado.trim() !== "" && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f1f8e9' }}>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={`Outro serviço prestado: ${formulario.outro_servico_prestado}`}
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                </Paper>
              </Grid>
            )}

            {formulario.servicos_prestados_descritivo && formulario.servicos_prestados_descritivo.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary" fontWeight="bold">
                    Total de serviços prestados: {formulario.servicos_prestados_descritivo.length}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default Step4View;
